import * as actionTypes from "../actions/actionTypes";

export const getLocalTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const initialState = {
  accessToken: null,
  authRedirectPath: "/",
  challengeName: null,
  daysUntilPasswordReset: null,
  error: null,
  errorType: null,
  forgotPasswordEmail: null,
  forgotPasswordStep: 1,
  loading: false,
  priInsBack: null,
  priInsFront: null,
  profile: null,
  referralCodeMailed: false,
  referralResponse: null,
  refreshToken: null,
  role: null,
  secInsBack: null,
  secInsFront: null,
  session: null,
  sessionTimer: null,
  sessionTimerId: null,
  showEmailVerified: false,
  showRsPwSuccess: false,
  timezone: getLocalTimezone(),
  token: null,
};

const authStart = (state, _action) => {
  return {
    ...state,
    error: null,
    loading: true,
  };
};

const authSuccess = (state, action) => {
  window.localStorage.setItem("token", action.idToken);
  window.localStorage.setItem("refreshToken", action.refreshToken);
  window.localStorage.setItem("accessToken", action.accessToken);

  return {
    ...state,
    accessToken: action.accessToken,
    authRedirectPath: action.redirectPath,
    daysUntilPasswordReset: action.daysUntilPasswordReset,
    error: null,
    loading: false,
    refreshToken: action.refreshToken,
    role: action.role,
    token: action.idToken,
  };
};

const pwResetRequired = (state, action) => {
  return {
    ...state,
    accessToken: action.accessToken,
    error: null,
    errorType: "PasswordResetRequired",
    loading: false,
    refreshToken: action.refreshToken,
    token: action.idToken,
  };
};

const authFail = (state, action) => {
  return {
    ...state,
    error: action.error,
    errorType: action.errorType,
    loading: false,
  };
};

const authChallenge = (state, action) => {
  return {
    ...state,
    authRedirectPath: action.redirectPath,
    challengeName: action.response.ChallengeName,
    error: null,
    loading: false,
    session: action.response.Session,
    username: action.response.ChallengeParameters.USER_ID_FOR_SRP,
  };
};

const clearAuthChallenge = (state, _action) => {
  return {
    ...state,
    authRedirectPath: initialState.redirectPath,
    challengeName: initialState.challengeName,
    session: initialState.session,
    username: null,
  };
};

const profileFetchSuccess = (state, action) => {
  return {
    ...state,
    profile: action.profile,
  };
};

const profileFetchFail = (state) => {
  return {
    ...state,
    profile: null,
  };
};

const loadToken = (state, action) => {
  return {
    ...state,
    accessToken: action.accessToken,
    authRedirectPath: action.redirectPath,
    error: null,
    loading: false,
    refreshToken: action.refreshToken,
    role: action.role,
    token: action.idToken,
  };
};

const refreshAccessToken = (state, action) => {
  window.localStorage.setItem("token", action.payload.IdToken);
  window.localStorage.setItem("accessToken", action.payload.AccessToken);

  return {
    ...state,
    accessToken: action.payload.AccessToken,
    token: action.payload.IdToken,
  };
};

const referralSuccess = (state, action) => {
  return {
    ...state,
    loading: true,
    referralResponse: action.formData,
  };
};

const referralStart = (state) => {
  return {
    ...state,
    loading: true,
  };
};

const referralFail = (state) => {
  return {
    ...state,
    loading: false,
    referralResponse: null,
  };
};
const signUpSuccess = (state) => {
  return {
    ...state,
    referralCodeMailed: true,
    referralResponse: null,
  };
};
const signUpFail = (state) => {
  return {
    ...state,
    referralCodeMailed: false,
  };
};

function clearSessionTimers(state) {
  if (state.sessionTimer) {
    clearInterval(state.sessionTimer);
    localStorage.removeItem(state.sessionTimerId);
  }
}

const logOut = (state, action) => {
  clearSessionTimers(state);
  Object.keys(localStorage)
    .filter((key) => !key.startsWith("timer-"))
    .forEach((key) => localStorage.removeItem(key));

  const profile = state.profile;

  if (profile && profile.idp && profile.idp_logout_url) {
    window.location.href = profile.idp_logout_url;

    return null;
  } else {
    return {
      ...state,
      accessToken: null,
      authRedirectPath: action.redirectPath,
      challengeName: null,
      profile: null,
      referralCodeMailed: false,
      referralResponse: null,
      refreshToken: null,
      role: null,
      session: null,
      sessionTimer: null,
      sessionTimerId: null,
      token: null,
      username: null,
    };
  }
};

const setSessionTimer = (state, action) => {
  return {
    ...state,
    sessionTimer: action.sessionTimer,
    sessionTimerId: action.sessionTimerId,
  };
};

const rsPwSent = (state, action) => {
  return {
    ...state,
    forgotPasswordEmail: action.payload,
    forgotPasswordStep: 2,
  };
};

const rsPw = (state) => {
  return {
    ...state,
    forgotPasswordEmail: null,
    forgotPasswordStep: 1,
    showRsPwSuccess: true,
  };
};

const rsResetSteps = (state) => {
  return {
    ...state,
    forgotPasswordStep: 1,
  };
};

const hideRsPwSuccess = (state) => {
  return {
    ...state,
    showRsPwSuccess: false,
  };
};

const setPriFrontInsImg = (state, action) => {
  return {
    ...state,
    priInsFront: {
      preview: action.payload.preview,
      url: action.payload.url,
    },
  };
};

const setPriBackInsImg = (state, action) => {
  return {
    ...state,
    priInsBack: {
      preview: action.payload.preview,
      url: action.payload.url,
    },
  };
};
const setSecFrontInsImg = (state, action) => {
  return {
    ...state,
    secInsFront: {
      preview: action.payload.preview,
      url: action.payload.url,
    },
  };
};

const setSecBackInsImg = (state, action) => {
  return {
    ...state,
    secInsBack: {
      preview: action.payload.preview,
      url: action.payload.url,
    },
  };
};

const unsetPriFrontInsImg = (state) => {
  return {
    ...state,
    priInsFront: null,
  };
};

const unsetPriBackInsImg = (state) => {
  return {
    ...state,
    priInsBack: null,
  };
};

const unsetSecFrontInsImg = (state) => {
  return {
    ...state,
    secInsFront: null,
  };
};

const unsetSecBackInsImg = (state) => {
  return {
    ...state,
    secInsBack: null,
  };
};

const unsetPriInsImg = (state) => {
  return {
    ...state,
    priInsBack: null,
    priInsFront: null,
  };
};

const unsetSecInsImg = (state) => {
  return {
    ...state,
    secInsBack: null,
    secInsFront: null,
  };
};

const showEmailVerified = (state, action) => {
  return {
    ...state,
    showEmailVerified: action.showEmailVerified,
  };
};

const hasActiveCard = (state) => {
  return {
    ...state,
    profile: {
      ...state.profile,
      has_active_card: true,
    },
  };
};

const updateJWT = (state, action) => {
  return {
    ...state,
    accessToken: action.accessToken,
    refreshToken: action.refreshToken,
    token: action.token,
  };
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.AUTH_START:
      return authStart(state, action);

    case actionTypes.AUTH_SUCCESS:
      return authSuccess(state, action);

    case actionTypes.AUTH_CHALLENGE:
      return authChallenge(state, action);

    case actionTypes.CLEAR_AUTH_CHALLENGE:
      return clearAuthChallenge(state, action);

    case actionTypes.AUTH_FAIL:
      return authFail(state, action);

    case actionTypes.PW_RESET_REQUIRED:
      return pwResetRequired(state, action);

    case actionTypes.REFRESH_ACCESS_TOKEN:
      return refreshAccessToken(state, action);

    case actionTypes.RS_PW_CODE_SENT:
      return rsPwSent(state, action);

    case actionTypes.RS_FORGOT_PW_STEPS:
      return rsResetSteps(state, action);

    case actionTypes.RS_PW_SUCCESS:
      return rsPw(state, action);

    case actionTypes.HIDE_RS_PW_SUCCESS:
      return hideRsPwSuccess(state, action);

    case actionTypes.LOAD_TOKEN:
      return loadToken(state, action);

    case actionTypes.PROFILE_FETCH_SUCCESS:
      return profileFetchSuccess(state, action);

    case actionTypes.PROFILE_FETCH_FAIL:
      return profileFetchFail(state, action);

    case actionTypes.REFERRAL_START:
      return referralStart(state, action);

    case actionTypes.REFERRAL_SUCCESS:
      return referralSuccess(state, action);

    case actionTypes.REFERRAL_FAIL:
      return referralFail(state, action);

    case actionTypes.SIGNUP_SUCCESS:
      return signUpSuccess(state, action);

    case actionTypes.SIGNUP_FAIL:
      return signUpFail(state, action);

    case actionTypes.LOG_OUT:
      return logOut(state, action);

    case actionTypes.SET_SESSION_TIMER:
      return setSessionTimer(state, action);

    case actionTypes.SET_FRONT_PRI_INS_IMAGE:
      return setPriFrontInsImg(state, action);

    case actionTypes.SET_BACK_PRI_INS_IMAGE:
      return setPriBackInsImg(state, action);

    case actionTypes.SET_FRONT_SEC_INS_IMAGE:
      return setSecFrontInsImg(state, action);

    case actionTypes.SET_BACK_SEC_INS_IMAGE:
      return setSecBackInsImg(state, action);

    case actionTypes.UNSET_FRONT_PRI_INS_IMAGE:
      return unsetPriFrontInsImg(state, action);

    case actionTypes.UNSET_BACK_PRI_INS_IMAGE:
      return unsetPriBackInsImg(state, action);

    case actionTypes.UNSET_FRONT_SEC_INS_IMAGE:
      return unsetSecFrontInsImg(state, action);

    case actionTypes.UNSET_BACK_SEC_INS_IMAGE:
      return unsetSecBackInsImg(state, action);

    case actionTypes.UNSET_PRI_INS_IMAGES:
      return unsetPriInsImg(state, action);

    case actionTypes.UNSET_SEC_INS_IMAGES:
      return unsetSecInsImg(state, action);

    case actionTypes.SHOW_EMAIL_VERIFIED:
      return showEmailVerified(state, action);

    case actionTypes.UPDATE_PATIENT_CARD_INFO:
      return hasActiveCard(state, action);

    case actionTypes.UPDATE_JWT:
      return updateJWT(state, action);

    default:
      return state;
  }
};

export default reducer;
